package org.multiplatform.kotfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("wasm_js_basic")
@Composable
fun BasicWasmJsPage() {
    BlogLayout(
        PageProperties(
            "WasmJs",
            ""),
        BlogProperties("WasmJs", "Basic")
    ) {
        Column(horizontalAlignment = Alignment.Start)
        {
            SpanText(
                text = "gradle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    kotlin {
                        wasmJs {
                            moduleName = "composeApp"
                            browser {
                                val projectDirPath = project.projectDir.path
                                commonWebpackConfig {
                                    outputFileName = "composeApp.js"
                                    devServer = (devServer ?: KotlinWebpackConfig.DevServer()).apply {
                                        static = (static ?: mutableListOf()).apply {
                                            // Serve sources to debug inside browser
                                            add(projectDirPath)
                                        }
                                    }
                                }
                            }
                            binaries.executable()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //gradle.properties
                    
                    kotlin.code.style=official

                    #Gradle
                    org.gradle.jvmargs=-Xmx2048M -Dfile.encoding=UTF-8 -Dkotlin.daemon.jvm.options\="-Xmx2048M"
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Animation
                    
                    fun defaultEnterAnim(
                        durationMillis: Int = 1000,
                        delayMillis: Int = 0,
                        easing: Easing = LinearEasing,
                        orientation: Orientation = Orientation.Vertical,
                        inverseSlide: Boolean = false
                    ): EnterTransition = fadeIn(
                        tween(
                            durationMillis = durationMillis,
                            delayMillis = delayMillis,
                            easing = easing
                        )
                    ) + when (orientation) {
                        Orientation.Horizontal -> slideInHorizontally(
                            animationSpec = tween(
                                durationMillis = durationMillis,
                                delayMillis = delayMillis,
                                easing = easing
                            ),
                            initialOffsetX = { if (inverseSlide) -it / 2 else it / 2 }
                        )

                        Orientation.Vertical -> slideInVertically(
                            animationSpec = tween(
                                durationMillis = durationMillis,
                                delayMillis = delayMillis,
                                easing = easing
                            ),
                            initialOffsetY = { if (inverseSlide) -it / 2 else it / 2 }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     //Device Check 
                     
                     typealias DeviceState = State<Device>

                     @Composable
                     fun rememberDeviceState(): DeviceState {
                         val screenWidthInDp = with(LocalDensity.current) { LocalScreenSize.current.width.toDp() }
                         return remember(screenWidthInDp) { derivedStateOf { Device.fromWidth(screenWidthInDp) } }
                     }

                     val DeviceState.isMobile get() = value == Device.Mobile
                     val DeviceState.isPc get() = value == Device.Pc
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //GridView 
                    
                    typealias DeviceState = State<Device>

                     @Composable
                     fun rememberDeviceState(): DeviceState {
                         val screenWidthInDp = with(LocalDensity.current) { LocalScreenSize.current.width.toDp() }
                         return remember(screenWidthInDp) { derivedStateOf { Device.fromWidth(screenWidthInDp) } }
                     }

                     val DeviceState.isMobile get() = value == Device.Mobile
                     val DeviceState.isPc get() = value == Device.Pc
                    
                    @Composable
                    fun AboutSection(modifier: Modifier = Modifier) {
                        val deviceState = rememberDeviceState()

                        val (gridColumns, gridModifier, gridSpacing) = when (deviceState.value) {
                            Device.Mobile -> {
                                Triple(2, Modifier.fillMaxWidth().aspectRatio(2 / 3f), 12.dp)
                            }

                            Device.Pc -> {
                                Triple(4, Modifier.width(720.dp).aspectRatio(2 / 1f), 12.dp)
                            }
                        }

                        LazyVerticalGrid(
                            columns = GridCells.Fixed(gridColumns),
                            contentPadding = PaddingValues(4.dp),
                            horizontalArrangement = Arrangement.spacedBy(gridSpacing),
                            verticalArrangement = Arrangement.spacedBy(gridSpacing),
                            modifier = gridModifier

                        ) {
                            items(Skill.entries.dropLast(if (deviceState.isMobile) 2 else 0)) {

                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //InteractionSource
                    
                    val interactionSource = remember { MutableInteractionSource() }
                    val isHovered by interactionSource.collectIsHoveredAsState()
                    val isPressed by interactionSource.collectIsPressedAsState()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun SectionColumn(
                        modifier: Modifier = Modifier,
                        deviceState: DeviceState,
                        backgroundColor: Color = MaterialTheme.colorScheme.primaryContainer,
                        horizontalAlignment: Alignment.Horizontal = Alignment.CenterHorizontally,
                        contentPadding: PaddingValues = when (deviceState.value) {
                            Device.Mobile -> mobileSectionPaddingValues
                            Device.Pc -> pcSectionPaddingValues
                        },
                        mobileContent: (@Composable ColumnScope.() -> Unit)? = null,
                        content: (@Composable ColumnScope.() -> Unit)? = null
                    ) {
                        MultiplatformTheme(
                            isDarkTheme = LocalThemeMode.current == ThemeMode.Dark
                        ) {
                            Box(
                                modifier = modifier.then(
                                    Modifier
                                        .background(color = backgroundColor)
                                        .fillMaxWidth()
                                ),
                                contentAlignment = Alignment.Center
                            ) {
                                Column(
                                    modifier = Modifier
                                        .padding(contentPadding)
                                        .then(if (deviceState.isMobile) Modifier else Modifier.width(PC_CONTENT_WIDTH.dp)),
                                    horizontalAlignment = horizontalAlignment,
                                    content = mobileContent?.takeIf { deviceState.isMobile } ?: content ?: {}
                                )
                            }
                        }
                    }

                    private val pcSectionPaddingValues = PaddingValues(
                        horizontal = PC_CONTENT_HORIZONTAL_PADDING.dp,
                        vertical = PC_CONTENT_VERTICAL_PADDING.dp
                    )

                    private val mobileSectionPaddingValues = PaddingValues(
                        horizontal = MOBILE_CONTENT_HORIZONTAL_PADDING.dp,
                        vertical = MOBILE_CONTENT_VERTICAL_PADDING.dp
                    )
                    """.trimIndent()
            )

            //https://github.com/subroh0508/portfolio
            KotlinCode(
                code = """
                    //Grid Layout
                    
                    internal object PortfolioTag {
                        const val CARD_GRID_COLUMN = "CardGridColumn"
                    }
                    
                    import androidx.compose.foundation.layout.*
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.platform.testTag
                    import androidx.compose.ui.unit.Dp
                    import androidx.compose.ui.unit.dp
                    import component.work.Work

                    private const val MaxGridCount = 2

                    @Composable
                    internal fun CardGrid(
                        minWidth: Dp,
                        works: List<Work>,
                        content: @Composable (Work) -> Unit,
                    ) = BoxWithConstraints {
                        val chunkedSize = (((maxWidth / (minWidth + 8.dp)).toInt()))
                            .takeIf { it in 1..MaxGridCount } ?: MaxGridCount
                        val chunkedWorks = works.toList().chunked(chunkedSize)

                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                        ) {
                            for (i in 0..<chunkedSize) {
                                Column(
                                    modifier = Modifier.weight(1F)
                                        .testTag(PortfolioTag.CARD_GRID_COLUMN),
                                    verticalArrangement = Arrangement.spacedBy(8.dp),
                                ) {
                                    chunkedWorks.forEach work@{ row ->
                                        content(row.getOrNull(i) ?: return@work)
                                    }
                                }
                            }
                        }
                    }
                    
                    internal val WorkCardMinWidth = 240.dp

                    private val WorkCardThumbnailHeight = 240.dp

                    @Composable
                    internal fun WorkCard(
                        work: Work,
                        modifier: Modifier = Modifier,
                    ) = WorkCard(
                        stringResource(work.headline),
                        stringResource(work.description),
                        stringResource(work.type.label),
                        work.time,
                        linkButtons = {
                            work.links.forEach {
                                LinkButton(
                                    it.href,
                                    stringResource(it.label),
                                    it.icon,
                                    it.contentDescription,
                                )
                            }
                        },
                        thumbnail = {
                            Image(
                                imageResource(work.thumbnail),
                                contentDescription = null,
                                contentScale = ContentScale.Crop,
                                modifier = Modifier.fillMaxSize(),
                            )
                        },
                        modifier = modifier,
                    )

                    @Composable
                    internal fun WorkCard(
                        headline: String,
                        description: String,
                        type: String,
                        time: Time,
                        linkButtons: @Composable () -> Unit,
                        thumbnail: @Composable () -> Unit,
                        modifier: Modifier = Modifier,
                    ) = ElevatedCard(modifier) {
                        WorkCardThumbnail(
                            type,
                            thumbnail,
                        )

                        Column(
                            modifier = Modifier.padding(16.dp),
                        ) {
                            WorkHeadline(
                                headline,
                                time,
                            )

                            Spacer(Modifier.height(16.dp))

                            Text(
                                text = description,
                                style = MaterialTheme.typography.bodyLarge,
                                modifier = Modifier.testTag(PortfolioTag.WORK_CARD_DESCRIPTION),
                            )

                            Spacer(Modifier.height(16.dp))

                            Row(
                                modifier = Modifier.align(Alignment.End),
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                            ) { linkButtons() }
                        }
                    }

                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    internal fun LinkButton(
                        href: String,
                        label: String,
                        icon: ImageVector,
                        contentDescription: String,
                    ) = TooltipBox(
                        positionProvider = TooltipDefaults.rememberPlainTooltipPositionProvider(),
                        state = rememberTooltipState(),
                        tooltip = { PlainTooltip { Text(label) } },
                    ) {
                        FilledTonalIconButton(
                            onClick = { openWindow(href) },
                            modifier = Modifier.pointerHoverIcon(PointerIcon.Hand),
                        ) {
                            Icon(
                                imageVector = icon,
                                contentDescription = contentDescription,
                            )
                        }
                    }

                    @Composable
                    private fun WorkCardThumbnail(
                        type: String,
                        thumbnail: @Composable () -> Unit,
                    ) = Box(
                        modifier = Modifier.fillMaxWidth()
                            .height(WorkCardThumbnailHeight),
                    ) {
                        thumbnail()

                        NoRippleAssistChip(
                            onClick = { },
                            colors = AssistChipDefaults.assistChipColors(
                                containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.8F),
                            ),
                            border = null,
                            leadingIcon = { Icon(Icons.Default.Tag, contentDescription = null) },
                            label = { Text(type) },
                            modifier = Modifier.align(Alignment.BottomEnd)
                                .offset(x = (-8).dp, y = (-4).dp)
                                .testTag(PortfolioTag.WORK_CARD_TAG),
                        )
                    }

                    @Composable
                    private fun WorkHeadline(
                        headline: String,
                        time: Time,
                    ) = Column {
                        Text(
                            text = headline,
                            style = MaterialTheme.typography.titleLarge,
                            modifier = Modifier.testTag(PortfolioTag.WORK_CARD_HEADLINE),
                        )

                        Spacer(Modifier.height(4.dp))

                        Text(
                            text = time.toString(),
                            color = MaterialTheme.colorScheme.outline,
                            style = MaterialTheme.typography.bodyMedium,
                            modifier = Modifier.testTag(PortfolioTag.WORK_CARD_TIME),
                        )
                    }
                    
                    @Composable
                    internal fun WorksSection(
                        visible: Boolean,
                    ) = AnimatedSection(
                        visible,
                        modifier = Modifier.padding(32.dp),
                    ) {
                        CardGrid(
                            WorkCardMinWidth,
                            Works,
                        ) { work ->
                            WorkCard(work)
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}
